/**
 * Hoja de prueba del papel de factura (bloque 11): imprime una factura de muestra con la plantilla de la sucursal,
 * llena hasta el último renglón, con reglas en milímetros y las líneas de los márgenes. Se imprime sobre una hoja
 * preimpresa ya anulada (o sobre una fotocopia) y se compara: si el texto cae sobre la cabecera o el pie, se ajustan
 * los milímetros en Configuración y se repite. Ruta /imprimir/PRUEBA/:sucursalId, solo para quien administra.
 */
import { Money, type DocumentoDTO, type EstructuraDTO } from '@mg/shared';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router';
import { useEmpresaImpresion } from '@/componentes/Impresion.tsx';
import { get } from '@/lib/api.ts';
import { FormatoFiscal } from './FormatoFiscal.tsx';

type Sucursal = EstructuraDTO['sucursales'][number];

/** Factura de muestra: tantos renglones como caben en la hoja, a Bs 1.000,00 cada uno, con IVA del 16 %. */
function muestra(s: Sucursal): DocumentoDTO {
  const n = s.plantilla_factura.renglones;
  const base = Money.parse('1000').mul(String(n));
  const iva = base.mul('0.16').redondear2();
  const lineas = Array.from({ length: n }, (_, i) => ({
    id: `r${i}`,
    producto_nombre: `RENGLÓN DE PRUEBA ${String(i + 1).padStart(2, '0')} · PRODUCTO DE EJEMPLO PARA CALIBRAR LA HOJA`,
    presentacion_nombre: 'unidad',
    unidades_presentacion: '1',
    cantidad: '1',
    unidades: '1',
    producto_unidad: 'und',
    precio_unitario_bs: '1000',
    descuento_pct: '0',
    subtotal_bs: '1000',
  }));
  // Solo los campos que usa el formato fiscal: es una hoja de calibración, no un documento
  return {
    id: 'prueba',
    tipo: 'FAC',
    sucursal_nombre: s.nombre,
    sucursal_formato_impresion: s.formato_impresion,
    sucursal_direccion_fiscal: s.direccion_fiscal,
    sucursal_telefono: s.telefono,
    sucursal_plantilla: s.plantilla_factura,
    numero_formateado: `${s.serie ? `${s.serie}-` : ''}000000`,
    control_formateado: '00-000000',
    documento_origen_numero: null,
    documento_origen_emitido_en: null,
    documento_origen_total_bs: null,
    cliente_razon_social: 'CLIENTE DE PRUEBA, C.A. (HOJA DE CALIBRACIÓN, SIN VALOR FISCAL)',
    cliente_rif: 'J-00000000-0',
    cliente_direccion_fiscal: 'DOMICILIO DE PRUEBA: PRIMERA LÍNEA DE LA DIRECCIÓN DEL CLIENTE, SEGUNDA LÍNEA DE LA DIRECCIÓN PARA VER DÓNDE TERMINA',
    cliente_telefono: '0000-0000000',
    condicion: 'contado',
    emitido_en: new Date().toISOString(),
    vence_en: null,
    descuento_pct: '0',
    iva_pct: '16',
    base_bs: base.toString(),
    exento_bs: '0',
    iva_bs: iva.toString(),
    total_bs: base.add(iva).toString(),
    pagos: [],
    lineas,
  } as unknown as DocumentoDTO;
}

export function HojaDePrueba() {
  const { sucursalId } = useParams<{ sucursalId: string }>();
  const est = useQuery({ queryKey: ['consolidado', 'estructura'], queryFn: () => get<EstructuraDTO>('/consolidado/estructura') });
  const empresa = useEmpresaImpresion();
  if (est.isLoading || empresa.isLoading) return <p style={{ padding: 24 }}>Preparando la hoja de prueba…</p>;
  const s = est.data?.sucursales.find((x) => x.id === sucursalId);
  if (!s || !empresa.data) return <p style={{ padding: 24 }} data-testid="prueba-sin-acceso">No se pudo cargar la plantilla de la sucursal.</p>;
  const p = s.plantilla_factura;
  return (
    <div data-testid="hoja-de-prueba">
      <style>{`@media print { .prueba-ayuda { display: none !important; } }`}</style>
      <div className="prueba-ayuda" style={{ fontFamily: 'system-ui, sans-serif', fontSize: 13, lineHeight: 1.45, background: '#fff', border: '1px solid #d1d5db', padding: '10px 14px', maxWidth: '215.9mm', margin: '12px auto 0', boxSizing: 'border-box' }}>
        <b>Hoja de prueba · {s.nombre}</b> ({s.formato_impresion === 'media_carta' ? 'media carta horizontal' : s.formato_impresion === 'media_carta_vertical' ? 'media carta en la mitad de arriba de una hoja carta de pie' : 'carta'}; superior {p.superior_mm} mm, izquierdo {p.izquierdo_mm} mm, derecho {p.derecho_mm} mm, inferior {p.inferior_mm} mm, {p.renglones} renglones).
        <ol style={{ margin: '6px 0 0', paddingLeft: 18 }}>
          <li>Imprime sobre una hoja preimpresa ya anulada o sobre una fotocopia de la hoja, con márgenes «Ninguno» y escala 100 %.</li>
          <li>El primer renglón («Cliente») debe quedar justo debajo de la cabecera de la imprenta, y «Total Bs.» justo encima del pie.</li>
          <li>Si queda alto o bajo, mide con la regla impresa cuántos milímetros hay que mover y cámbialos en Configuración. Repite hasta que calce.</li>
        </ol>
        <button type="button" onClick={() => window.print()} style={{ marginTop: 8, padding: '4px 12px', cursor: 'pointer' }} data-testid="prueba-imprimir">Imprimir hoja de prueba</button>
      </div>
      <FormatoFiscal d={muestra(s)} e={empresa.data} reglas />
    </div>
  );
}
